/**
 * Label Service
 *
 * Fetches address labels via Cloud Function with ENS fallback.
 * Returns the first available label from:
 * 1. labels[0].label
 * 2. entities[0].entity
 * 3. erc20TokenMetadata[0].name
 * 4. ENS reverse lookup (if no names found)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 1 day in milliseconds

// In-memory cache: { cacheKey: { label: 'Vitalik', timestamp: 1234567890 } }
const labelCache = new Map();

// Track pending requests to prevent duplicate API calls (especially during React StrictMode)
const pendingRequests = new Map();

/**
 * Fetch label via Cloud Function (includes ENS fallback)
 * Cloud function searches across all EVM chains for label data.
 * If user's chain has no label, it returns label from another chain with substituteChain field.
 * @param {string} address - Normalized address (lowercase)
 * @param {string} chain - Chain name (e.g., 'ethereum', 'polygon')
 * @returns {Promise<{label: string|null, entity: string|null, logo: string|null, erc20TokenName: string|null, erc20TokenSymbol: string|null, ens: string|null, substituteChain: string|null}>}
 */
const fetchLabelFromCloudFunction = async (address, chain) => {
  const emptyResult = {label: null, entity: null, logo: null, erc20TokenName: null, erc20TokenSymbol: null, ens: null, substituteChain: null};
  try {
    const getAddressLabel = httpsCallable(functions, 'getAddressLabel');
    const response = await getAddressLabel({ address, chain });
    const result = response?.data;  // Firebase callable returns { data: {...} }

    if (!result?.success) {
      console.warn('getAddressLabel returned unsuccessful:', result);
      return emptyResult;
    }
    // console.log(`Result of getAddressLabel:\n${JSON.stringify(result, null, 2)}`);
    return {
      label: result.label || null,
      entity: result.entity || null,
      logo: result.logo || null,
      erc20TokenName: result.erc20TokenName || null,
      erc20TokenSymbol: result.erc20TokenSymbol || null,
      ens: result.ens || null,
      substituteChain: result.substituteChain || null  // Chain where label was found (if different from requested)
    };
  } catch (error) {
    console.warn('Error fetching label from Cloud Function:', error);
    return emptyResult;
  }
};

/**
 * Get label for an address
 * Uses local cache with 1 day expiry
 * Cloud function searches across all EVM chains - if requested chain has no label,
 * it returns label from another chain with substituteChain field.
 * @param {string} address - Address (0x... for EVM)
 * @param {string} chain - Chain name (e.g., 'ethereum', 'polygon')
 * @returns {Promise<{label: string|null, entity: string|null, logo: string|null, erc20TokenName: string|null, erc20TokenSymbol: string|null, ens: string|null, substituteChain: string|null}>}
 */
export const getLabel = async (address, chain = 'ethereum') => {
  const emptyResult = { label: null, entity: null, logo: null, erc20TokenName: null, erc20TokenSymbol: null, ens: null, substituteChain: null };

  try {
    // Validate address format (basic check for EVM addresses)
    if (!address || !address.match(/^0x[a-fA-F0-9]{40}$/)) {
      return emptyResult;
    }

    // Normalize address to lowercase for cache key
    const normalizedAddress = address.toLowerCase();
    const cacheKey = `${chain}_${normalizedAddress}`;

    // 1. Check local cache first
    if (labelCache.has(cacheKey)) {
      const cached = labelCache.get(cacheKey);
      const age = Date.now() - cached.timestamp;
      if (age < CACHE_DURATION) {
        // Cache hit - return cached value
        return cached.data;
      } else {
        // Cache expired - remove it
        labelCache.delete(cacheKey);
      }
    }

    // Check if request is already pending (prevents duplicate API calls during React StrictMode)
    if (pendingRequests.has(cacheKey)) {
      // Reuse the pending promise instead of making a new request
      return await pendingRequests.get(cacheKey);
    }

    // Create new request promise
    const requestPromise = (async () => {
      try {
        const result = await fetchLabelFromCloudFunction(normalizedAddress, chain);

        // Cache the result
        labelCache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      } finally {
        // Clean up pending request
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store pending request to reuse it if another call comes in
    pendingRequests.set(cacheKey, requestPromise);

    // Return the promise result
    return await requestPromise;
  } catch (error) {
    // Outer catch for validation errors
    return emptyResult;
  }
};

/**
 * Clear label cache (useful for testing or manual refresh)
 */
export const clearLabelCache = () => {
  labelCache.clear();
};

/**
 * format address's human readable
 * 1) Entity level: coin -> entity -> token name(symbol)
 * 2) Address level: private alias -> label -> ENS
 * Display: {entity} • {address} if both available
 */
export const formatAddressHumanReadable = (
  matchedCoin, labelResult, privateAlias, token
) => {
  const logo = matchedCoin?.image || labelResult?.logo || (token?.native_token === false ? (token?.logo || token?.thumbnail) : null) || null;
  const symbolName = matchedCoin?.symbol || labelResult?.erc20TokenSymbol || (token?.native_token === false ? token?.symbol : null) || null;

  let entityName = null;
  let entityNameSource = null;
  if (matchedCoin?.name) {
    entityName = matchedCoin.name;
    entityNameSource = 'Coin name';
  } else if (labelResult?.erc20TokenName) {
    entityName = labelResult.erc20TokenName;
    entityNameSource = 'Token name';
  } else if (token?.native_token === false && token?.name) {
    entityName = token.name;
    entityNameSource = 'Token name from on-chain data';
  } else if (labelResult?.entity) {
    entityName = labelResult.entity;
    entityNameSource = 'Entity';
  }

  let addressName = null;
  let addressNameSource = null;
  if (privateAlias) {
    addressName = privateAlias;
    addressNameSource = 'Your private alias';
  } else if (labelResult?.label) {
    addressName = labelResult.label;
    addressNameSource = 'Label';
  } else if (labelResult?.ens) {
    addressName = labelResult.ens;
    addressNameSource = 'ENS';
  }

  return {
    entityName, entityNameSource, symbolName, logo,
    addressName, addressNameSource,
  }
};
